import { z } from 'zod';
import { zText } from './primitives';

/**
 * 'users' are the restaurant owner(s) and staff. They can have multiple restaurants.
 * These are different to 'customers' who are the end users of the app.
 * 'phoneNumber' is managed via AWS Cognito and not stored in Convex.
 */

export const USER_ROLES = ['OWNER', 'STAFF'] as const;
export const zRole = z.literal(USER_ROLES);

export const restaurantUserSchema = z.object({
  name: z.string().default('Owner'),

  // permissions: an array, can view X reports, can open drawer etc <= only set by OWNER.
  // Use AWS style. "sales:view", "discounts:create" etc
  role: zRole.default('OWNER'),

  /**
   * - `admin-native` push notifications only.
   * - `pos-native` can have many devices, each with their own push notification config.
   */
  adminNativePushNotificationConfigId: z.string().nullable().default(null).optional(),
  //adminNativeDevicePlatform: z.union([z.literal('ios'), z.literal('android')]).optional(),
  //adminNativeLastLoggedInAt: z.number().optional(),

  // Derived from Cognito user attributes
  isEnabled: z.boolean().optional(),
  sub: z
    .string()
    .describe('User identity `subject` or AWS Cognito `sub` user attribute')
    .optional(),
  username: z.string().describe("Read only. Derived from Cognito 'username'.").optional(),
  /**
   * `phoneNumber` is read only. Derived from Cognito user 'phone_number' attribute.
   * In international format, E.g. +61466308221"
   */
  phoneNumber: z.string().optional(),

  restaurantIds: z
    .array(z.string())
    .optional()
    .describe('The restaurant(s) the user has access to.'),

  /**
   * CRM-style internal notes about this user. Single free-form text blob,
   * edited as a whole. Both humans and the AI agent write here, so every save
   * also appends a snapshot to `restaurantUserNoteVersions` for audit + revert.
   *
   * The note text may reference one of the user's restaurants by name when
   * relevant, since one user can manage many via `restaurantIds`.
   */
  notes: z.string().nullable().default(null).optional(),
  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});
export type RestaurantUser = z.output<typeof restaurantUserSchema>;

/** Strict schema */
export const restaurantUserStrictSchema = restaurantUserSchema.safeExtend({
  name: zText,
  notes: zText,
});

/** Convex schema */
export const restaurantUserCvxSchema = restaurantUserSchema.safeExtend({
  // Derived from Cognito user attributes
  isEnabled: z.boolean(),
  sub: z.string(),
  username: z.string(),
  phoneNumber: z.string(),
  restaurantIds: z.array(z.string()),

  _creationTime: z.number(),
  _id: z.string(),
});
export type RestaurantUserCvx = z.output<typeof restaurantUserCvxSchema>;
